import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  BookMarked,
  BookOpen,
  Calculator,
  Percent,
  Clock,
  FileSignature,
  FileSpreadsheet,
  FileText,
  Gem,
  HandCoins,
  Coins,
  Wrench,
  Banknote,
  Users2,
  UserCheck,
  IdCard,
  Landmark,
  AlertCircle,
  Activity,
  LayoutDashboard,
  LogOut,
  Menu,
  PauseCircle,
  Receipt,
  ScrollText,
  ShieldCheck,
  Store,
  TrendingUp,
  Tv,
  UserRound,
  Users as UsersIcon,
  Webhook,
  X,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { VERSION_LABEL } from "@/lib/version";
import { RatePrompt } from "@/components/RatePrompt";
import { api, roleLabel } from "@/lib/api";

const navItems = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard", end: true },
  { to: "/new/tax_invoice", label: "New Tax Invoice", icon: Receipt, testid: "nav-new-tax-invoice" },
  { to: "/new/gold_bullion", label: "New Bullion Invoice", icon: Gem, testid: "nav-new-bullion" },
  { to: "/new/loan", label: "New Loan Agreement", icon: HandCoins, testid: "nav-new-loan", perm: "documents:create" },
  { to: "/new/buying_contract", label: "New Buying Contract", icon: ScrollText, testid: "nav-new-buying" },
  { to: "/parked", label: "Parked / On Hold", icon: PauseCircle, testid: "nav-parked" },
  { to: "/loans", label: "Active Loans", icon: HandCoins, testid: "nav-active-loans", perm: "loans:update" },
  { to: "/repairs", label: "Repairs", icon: Wrench, testid: "nav-repairs" },
  { to: "/queue", label: "Queue", icon: Users2, testid: "nav-queue" },
  { to: "/kyc", label: "KYC Forms", icon: IdCard, testid: "nav-kyc" },
  { to: "/reports/cash-watch", label: "Cash Watch", icon: Banknote, testid: "nav-cash-watch" },
  { to: "/reports/money-owed", label: "Money Owed", icon: AlertCircle, testid: "nav-money-owed", perm: "financials:read" },
  { to: "/kuber/payments", label: "Kuber Payments", icon: Landmark, testid: "nav-kuber-payments", badge: "kuber" },
  { to: "/documents", label: "All Records", icon: FileText, testid: "nav-documents" },
  { to: "/customers", label: "Customers", icon: UserRound, testid: "nav-customers" },
  { to: "/catalogue", label: "Item Catalogue", icon: BookMarked, testid: "nav-catalogue" },
  { to: "/reports", label: "Accountant Export", icon: FileSpreadsheet, testid: "nav-reports", end: true, perm: "financials:read" },
  { to: "/reports/old-gold", label: "Old Gold Report", icon: Coins, testid: "nav-old-gold", perm: "financials:read" },
  { to: "/reports/staff", label: "Staff Takings", icon: UserCheck, testid: "nav-staff-takings", perm: "audit:read" },
  { to: "/reports/queue-waits", label: "Queue Wait Times", icon: Clock, testid: "nav-queue-waits", perm: "audit:read" },
  { to: "/reports/discounts", label: "Discounts Given", icon: Percent, testid: "nav-discounts", perm: "audit:read" },
  { to: "/gold-rates", label: "Today's Gold Rate", icon: TrendingUp, testid: "nav-gold-rates", perm: "rates:write" },
  { to: "/terms", label: "Terms & Conditions", icon: FileSignature, testid: "nav-terms", perm: "terms:write" },
  { to: "/stores", label: "Store Details", icon: Store, testid: "nav-stores", perm: "stores:write" },
  { to: "/users", label: "Staff Accounts", icon: UsersIcon, testid: "nav-users", perm: "users:manage" },
  { to: "/integrations/kuber", label: "Kuber Webhook", icon: Webhook, testid: "nav-kuber", perm: "users:manage" },
  { to: "/audit", label: "Audit Trail", icon: ShieldCheck, testid: "nav-audit", perm: "audit:read" },
  { to: "/monitoring", label: "System Monitoring", icon: Activity, testid: "nav-monitoring", perm: "users:manage" },
  { to: "/guide", label: "How to use this", icon: BookOpen, testid: "nav-guide" },
  { to: "/calculator", label: "Ex-GST Calculator", icon: Calculator, testid: "nav-calculator" },
  { to: "/calculations", label: "How figures are worked out", icon: Calculator, testid: "nav-calculations", perm: "users:manage" },
  { to: "/display/DANDENONG", label: "TV Board · Dandenong", icon: Tv, testid: "nav-display-dandenong", external: true },
  { to: "/display/TRUGANINA", label: "TV Board · Truganina", icon: Tv, testid: "nav-display-truganina", external: true },
];

export default function Layout() {
  const { user, logout, branch, branches, switchBranch, can } = useAuth();
  const lockedToBranch = !!user && !["admin", "it_admin"].includes(user.role) && !!user.branch;
  const navigate = useNavigate();
  const [hovering, setHovering] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [kuberPending, setKuberPending] = useState(0);

  useEffect(() => {
    const poll = () =>
      api
        .get("/kuber/payments/pending-count")
        .then(({ data }) => setKuberPending(data.pending || 0))
        .catch(() => {});
    poll();
    const t = setInterval(poll, 60000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    localStorage.removeItem("dhunna-sidebar-mode");
    localStorage.removeItem("dhunna-sidebar-collapsed");
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const visible = navItems.filter((n) => !n.perm || can(n.perm));
  // The menu stays out of the way as a narrow icon rail and slides out on hover,
  // so the invoice screens get the full width of the monitor.
  const floating = !mobileOpen;
  const expanded = floating && hovering;
  const rail = !expanded && !mobileOpen;

  const linkClass = ({ isActive }) =>
    `flex items-center gap-3 px-3 py-2.5 text-sm transition-colors duration-150 ${
      rail ? "justify-center" : ""
    } ${
      isActive
        ? "bg-[#C5A059] font-semibold text-[#0A0A0A]"
        : "text-[#C9CBCF] hover:bg-[#161616] hover:text-white"
    }`;

  return (
    <div className="App flex min-h-screen bg-white">
      {mobileOpen && (
        <button
          type="button"
          aria-label="Close menu"
          className="no-print fixed inset-0 z-40 bg-black/60 lg:hidden"
          onClick={() => setMobileOpen(false)}
          data-testid="sidebar-backdrop"
        />
      )}

      {floating && (
        <div
          className="no-print hidden lg:block lg:w-[68px] lg:shrink-0"
          data-testid="sidebar-spacer"
        />
      )}

      <aside
        onMouseEnter={() => floating && setHovering(true)}
        onMouseLeave={() => floating && setHovering(false)}
        className={`no-print flex-col border-r border-[#1f1f1f] bg-[#0A0A0A] transition-[width] duration-200
          ${mobileOpen ? "fixed inset-y-0 left-0 z-50 flex w-64" : "hidden lg:flex"}
          ${floating
            ? `lg:fixed lg:left-0 lg:top-0 lg:z-40 lg:h-screen ${
                hovering ? "lg:w-64 lg:shadow-2xl lg:shadow-black/40" : "lg:w-[68px]"
              }`
            : `lg:sticky lg:top-0 lg:h-screen lg:shrink-0 ${rail ? "lg:w-[68px]" : "lg:w-64"}`}`}
        data-testid="sidebar"
        data-sidebar-mode="auto"
      >
        <div className="flex items-center justify-between px-4 py-6">
          <Link to="/" className="block" data-testid="sidebar-logo">
            <div className="flex items-center gap-3">
              <img
                src="/brand/logo.png"
                alt="Dhunna Jewellers"
                className={`w-auto shrink-0 object-contain ${rail ? "h-8" : "h-12"}`}
              />
              {!rail && (
                <div>
                  <div className="font-invoice text-xl leading-none text-white">DHUNNA</div>
                  <div className="mt-1 text-[9px] font-semibold tracking-[0.3em] text-[#C5A059]">
                    JEWELLERS
                  </div>
                </div>
              )}
            </div>
          </Link>
          {mobileOpen && (
            <button
              type="button"
              onClick={() => setMobileOpen(false)}
              className="p-1.5 text-[#C9CBCF] hover:text-white lg:hidden"
              data-testid="close-sidebar-button"
              aria-label="Close menu"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>
        {!rail && <div className="gold-rule mx-6" />}
        <nav className="mt-4 flex-1 space-y-0.5 overflow-y-auto px-3 pb-4">
          {visible.map(({ to, label, icon: Icon, testid, end, external, badge }) => external ? (
            <a
              key={to}
              href={to}
              target="_blank"
              rel="noreferrer"
              data-testid={testid}
              title={rail ? label : undefined}
              onClick={() => setMobileOpen(false)}
              className={linkClass({ isActive: false })}
            >
              <Icon className="h-4 w-4 shrink-0" />
              {!rail && label}
            </a>
          ) : (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={testid}
              title={rail ? label : undefined}
              onClick={() => setMobileOpen(false)}
              className={linkClass}
            >
              <Icon className="h-4 w-4 shrink-0" />
              {!rail && label}
              {badge === "kuber" && kuberPending > 0 && (
                <span
                  className="ml-auto bg-[#C5A059] px-1.5 text-[11px] font-bold text-[#0A0A0A]"
                  data-testid="nav-kuber-badge"
                >
                  {kuberPending}
                </span>
              )}
            </NavLink>
          ))}
        </nav>
        {!rail && (
          <div className="border-t border-[#1f1f1f] p-4 text-xs text-[#8A8D93]">
            <span data-testid="app-version">
              <Link to="/release-notes" className="underline hover:text-[#C5A059]">
                {VERSION_LABEL}
              </Link>
            </span>
            {" · "}
            Excel-free invoicing since 2026
            {" · "}
            <a href="/privacy" target="_blank" rel="noreferrer"
               className="underline hover:text-[#C5A059]" data-testid="footer-privacy-link">
              Privacy
            </a>
          </div>
        )}
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header
          className="no-print sticky top-0 z-30 flex flex-wrap items-center justify-between gap-3 border-b border-border bg-white/80 px-5 py-3 backdrop-blur-xl sm:px-8"
          data-testid="topbar"
        >
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setMobileOpen(true)}
              className="p-1.5 text-muted-foreground transition-colors duration-150 hover:text-foreground lg:hidden"
              data-testid="open-sidebar-button"
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5" />
            </button>
            <span className="hidden text-[11px] font-semibold uppercase tracking-widest text-muted-foreground sm:inline">
              Branch
            </span>
            <Select value={branch} onValueChange={switchBranch} disabled={lockedToBranch}>
              <SelectTrigger
                className="h-9 w-[150px] rounded-none border-input text-sm sm:w-[190px]"
                data-testid="branch-switcher"
                title={lockedToBranch ? "Your account is set to this store" : undefined}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-none bg-white">
                {branches.includes("DANDENONG") && (
                  <SelectItem value="DANDENONG" data-testid="branch-option-dandenong">
                    Dandenong
                  </SelectItem>
                )}
                {branches.includes("TRUGANINA") && (
                  <SelectItem value="TRUGANINA" data-testid="branch-option-truganina">
                    Truganina
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-4">
            <Link
              to="/my-account"
              className="text-right leading-tight hover:text-[#B38F4E]"
              data-testid="current-user"
            >
              <div className="text-sm font-semibold">{user?.name}</div>
              <div className="text-[11px] uppercase tracking-wider text-[#B38F4E]">
                {roleLabel(user?.role)}
              </div>
            </Link>
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
              data-testid="logout-button"
              className="rounded-none border-input"
            >
              <LogOut className="mr-1.5 h-3.5 w-3.5" /> Logout
            </Button>
          </div>
        </header>
        <main className="min-w-0 flex-1 px-5 py-7 sm:px-8 sm:py-10">
          <Outlet />
        </main>
        <RatePrompt />
      </div>
    </div>
  );
}
